/**
 * Platform Debugger Component
 * Показывает информацию о текущей платформе (только в dev режиме)
 */

import React, { useState, useEffect } from 'react';
import { detectPlatformMode, getSafeAreaInsets, isTelegramApp, isPWAMode, isSafari } from '../utils/platform';
import './PlatformDebugger.css';

export const PlatformDebugger: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [platformInfo, setPlatformInfo] = useState({
    mode: '',
    isTelegram: false,
    isPWA: false,
    isSafariBrowser: false,
    safeAreas: { top: 0, bottom: 0, left: 0, right: 0 },
    viewport: { width: 0, height: 0 },
    cssVars: {
      paddingTop: '',
      paddingBottom: '',
      paddingHorizontal: '',
    }
  });

  useEffect(() => {
    updatePlatformInfo();
    
    // Обновляем при изменении размера окна
    const handleResize = () => updatePlatformInfo();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const updatePlatformInfo = () => {
    const mode = detectPlatformMode();
    const safeAreas = getSafeAreaInsets();
    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);
    
    setPlatformInfo({
      mode,
      isTelegram: isTelegramApp(),
      isPWA: isPWAMode(),
      isSafariBrowser: isSafari(),
      safeAreas,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      cssVars: {
        paddingTop: computedStyle.getPropertyValue('--platform-padding-top').trim(),
        paddingBottom: computedStyle.getPropertyValue('--platform-padding-bottom').trim(),
        paddingHorizontal: computedStyle.getPropertyValue('--platform-padding-horizontal').trim(),
      }
    });
  };

  // Показываем только в dev режиме или по комбинации клавиш
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ctrl/Cmd + Shift + P
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'P') {
        setIsVisible(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  if (!isVisible) {
    return (
      <button 
        className="platform-debugger__toggle"
        onClick={() => setIsVisible(true)}
        title="Показать информацию о платформе (Ctrl+Shift+P)"
      >
        🔧
      </button>
    );
  }

  const getModeColor = () => {
    switch (platformInfo.mode) {
      case 'telegram': return '#0088cc';
      case 'pwa-standalone': return '#5856D6';
      case 'browser-safari': return '#007AFF';
      case 'browser-other': return '#34C759';
      default: return '#8E8E93';
    }
  };

  const getModeLabel = () => {
    switch (platformInfo.mode) {
      case 'telegram': return 'Telegram Mini App';
      case 'pwa-standalone': return 'PWA Standalone';
      case 'browser-safari': return 'Safari Browser';
      case 'browser-other': return 'Other Browser';
      default: return 'Unknown';
    }
  };

  return (
    <div className="platform-debugger">
      <div className="platform-debugger__header">
        <h3>Platform Info</h3>
        <button 
          className="platform-debugger__close"
          onClick={() => setIsVisible(false)}
        >
          ×
        </button>
      </div>

      <div className="platform-debugger__content">
        {/* Mode */}
        <div className="platform-debugger__section">
          <div 
            className="platform-debugger__badge"
            style={{ backgroundColor: getModeColor() }}
          >
            {getModeLabel()}
          </div>
        </div>

        {/* Flags */}
        <div className="platform-debugger__section">
          <h4>Flags</h4>
          <div className="platform-debugger__flags">
            <span className={platformInfo.isTelegram ? 'active' : ''}>
              Telegram: {platformInfo.isTelegram ? '✅' : '❌'}
            </span>
            <span className={platformInfo.isPWA ? 'active' : ''}>
              PWA: {platformInfo.isPWA ? '✅' : '❌'}
            </span>
            <span className={platformInfo.isSafariBrowser ? 'active' : ''}>
              Safari: {platformInfo.isSafariBrowser ? '✅' : '❌'}
            </span>
          </div>
        </div>

        {/* Safe Areas */}
        <div className="platform-debugger__section">
          <h4>Safe Areas</h4>
          <div className="platform-debugger__grid">
            <div>Top: {platformInfo.safeAreas.top}px</div>
            <div>Bottom: {platformInfo.safeAreas.bottom}px</div>
            <div>Left: {platformInfo.safeAreas.left}px</div>
            <div>Right: {platformInfo.safeAreas.right}px</div>
          </div>
        </div>

        {/* Viewport */}
        <div className="platform-debugger__section">
          <h4>Viewport</h4>
          <div className="platform-debugger__grid">
            <div>Width: {platformInfo.viewport.width}px</div>
            <div>Height: {platformInfo.viewport.height}px</div>
          </div>
        </div>

        {/* CSS Variables */}
        <div className="platform-debugger__section">
          <h4>CSS Variables</h4>
          <div className="platform-debugger__vars">
            <div>--platform-padding-top: <code>{platformInfo.cssVars.paddingTop}</code></div>
            <div>--platform-padding-bottom: <code>{platformInfo.cssVars.paddingBottom}</code></div>
            <div>--platform-padding-horizontal: <code>{platformInfo.cssVars.paddingHorizontal}</code></div>
          </div>
        </div>

        {/* Body Classes */}
        <div className="platform-debugger__section">
          <h4>Body Classes</h4>
          <div className="platform-debugger__classes">
            {Array.from(document.body.classList)
              .filter(cls => cls.startsWith('platform-'))
              .map(cls => (
                <span key={cls} className="platform-debugger__class">
                  {cls}
                </span>
              ))}
          </div>
        </div>

        {/* Refresh Button */}
        <button 
          className="platform-debugger__refresh"
          onClick={updatePlatformInfo}
        >
          🔄 Обновить
        </button>
      </div>

      <div className="platform-debugger__hint">
        Нажмите Ctrl+Shift+P чтобы скрыть
      </div>
    </div>
  );
};

